import { forwardRef, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import { sanitizeReadonlyArray } from "~/lib/utils";
import type { magazinesStyleLeadContentItemContent$key } from "~/queries/__generated__/magazinesStyleLeadContentItemContent.graphql";

import {
  AuthorNameContainer,
  AuthorNameWrapper,
  Container,
  Cover,
  Headline,
  StoryLabel,
  StyledAuthorName,
  StyledEntityLink,
} from "./styles";

export type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  reference: magazinesStyleLeadContentItemContent$key;
};

export const ContentItemMagazinesStyleLead = forwardRef<HTMLDivElement, Props>(
  ({ className, provider, reference }, elementReference) => {
    const article = useFragment(
      graphql`
        fragment magazinesStyleLeadContentItemContent on Content {
          authors {
            ...authorNameAuthor
          }
          ...entityLink
        }
      `,
      reference,
    );

    const cover = (
      <StyledEntityLink reference={article}>
        {provider.coverImage({
          responsiveVariants: {
            desktopUp: "size1280x720",
            mobileUp: "size540x360",
            tabletUp: "size1280x720", // TODO: update variant when image-style available
          },
        })}
        {provider.video?.durationOverlay({
          responsiveVariants: { desktopUp: "large", mobileUp: "large", tabletUp: "large" },
        })}
      </StyledEntityLink>
    );
    const headline = (
      <StyledEntityLink reference={article}>
        {provider.headline({ preferSocialHeadline: true })}
      </StyledEntityLink>
    );

    const authors = useMemo(() => sanitizeReadonlyArray(article.authors), [article.authors]);
    const isMultipleAuthors = authors.length > 1;

    return (
      <Container className={className} ref={elementReference}>
        <Cover>{cover}</Cover>
        <Headline>{headline}</Headline>
        <AuthorNameContainer $isMultipleAuthors={isMultipleAuthors}>
          <StoryLabel>story</StoryLabel>
          <span>
            {authors.map((author, index) => (
              <AuthorNameWrapper key={index}>
                <StyledAuthorName reference={author} />
              </AuthorNameWrapper>
            ))}
          </span>
        </AuthorNameContainer>
      </Container>
    );
  },
);

ContentItemMagazinesStyleLead.displayName = "ContentItemMagazinesStyleLead";
